// JD (Joint Director) API Service
// Handles all API calls for Joint Director dashboard functionality

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class JDApiService {
    constructor() {
        // Setup axios instance with default config
        this.apiClient = axios.create({
            baseURL: API_BASE_URL,
            timeout: 30000,
            headers: {
                'Content-Type': 'application/json'
            }
        });

        // Add request interceptor to include auth token
        this.apiClient.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('authToken');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Add response interceptor for error handling
        this.apiClient.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    // Handle unauthorized access
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('userData');
                    window.location.href = '/login';
                }
                return Promise.reject(error);
            }
        );
    }

    /**
     * Validate user session
     */
    validateUserSession() {
        const userData = localStorage.getItem('userData');
        const authToken = localStorage.getItem('authToken');
        return userData && authToken;
    }

    /**
     * Get applications list based on type
     * Equivalent to .NET rptrAppsList_DataBind method
     */
    async getApplicationsList({ appType, orgId, userTypeId, page = 1, limit = 50, searchKeyword = '', dealingOfficer = null, stage = null }) {
        try {
            const response = await this.apiClient.post('/jd/applications/list', {
                appType,
                orgId,
                userTypeId,
                page,
                limit,
                searchKeyword,
                dealingOfficer,
                stage
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Get applications list error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to fetch applications'
            };
        }
    }

     async getGroupApplicationsList({ appType, orgId, userTypeId, page = 1, limit = 50, searchKeyword = '', dealingOfficer = null, stage = null }) {
        try {
            const response = await this.apiClient.post('/jd/groupapplications/list', {
                appType,
                orgId,
                userTypeId,
                page,
                limit,
                searchKeyword,
                dealingOfficer,
                stage
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Get applications list error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to fetch applications'
            };
        }
    }

    async getApplicationsListInActive({ appType, orgId, userTypeId, page = 1, limit = 50, searchKeyword = '', dealingOfficer = null, stage = null }) {
        try {
            const response = await this.apiClient.post('/jd/applications/inActive', {
                appType,
                orgId,
                userTypeId,
                page,
                limit,
                searchKeyword,
                dealingOfficer,
                stage
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Get applications list error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to fetch applications'
            };
        }
    }
    async getApplicationsListExtend({ appType, orgId, userTypeId, page = 1, limit = 50, searchKeyword = '', dealingOfficer = null, stage = null }) {
        try {
            const response = await this.apiClient.post('/jd/applications/extend', {
                appType,
                orgId,
                userTypeId,
                page,
                limit,
                searchKeyword,
                dealingOfficer,
                stage
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Get applications list error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to fetch applications'
            };
        }
    }

    async inactiveRemarks({ appId }) {
        try {
            const response = await this.apiClient.get(`/jd/inactiveRemarks?appId=${appId}`);
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Get applications list error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to fetch applications'
            };
        }
    }
    async extendRemarks({ appId }) {
        try {
            const response = await this.apiClient.get(`/jd/extendRemarks?appId=${appId}`);
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Get applications list error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to fetch applications'
            };
        }
    }

    async acceptInactive(data) {
        const response = await this.apiClient.post(
            "/jd/acceptInactive",
            data
        );
        return response.data;
    }
    async acceptExtend(data) {
        const response = await this.apiClient.post(
            "/jd/acceptExtend",
            data
        );
        return response.data;
    }

    async rejectExtend(data) {
        const response = await this.apiClient.post(
            "/jd/rejectExtend",
            data
        );
        return response.data;
    }
    async rejectInactive(data) {
        const response = await this.apiClient.post(
            "/jd/rejectInactive",
            data
        );
        return response.data;
    }

    /**
     * Get joint directors/dealing officers list
     * Equivalent to .NET bindJoindDirectors method
     */
    async getJointDirectors({ orgId, userTypeId }) {
        try {
            const response = await this.apiClient.post('/jd/joint-directors', {
                orgId,
                userTypeId
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Get joint directors error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to fetch joint directors'
            };
        }
    }
    async getJointDirectorsDo({ orgId, userTypeId }) {
        try {
            const response = await this.apiClient.post('/jd/joint-directors-do', {
                orgId,
                userTypeId
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Get joint directors error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to fetch joint directors'
            };
        }
    }

    /**
     * Get dealing officers for filter
     * Equivalent to .NET BindOfficers method
     */
    async getDealingOfficers({ orgId, userTypeId }) {
        try {
            const response = await this.apiClient.post('/jd/dealing-officers', {
                orgId,
                userTypeId
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Get dealing officers error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to fetch dealing officers'
            };
        }
    }

      async getDealingOfficersDO({ orgId, userTypeId }) {
        try {
            const response = await this.apiClient.post('/jd/dealing-officers-do', {
                orgId,
                userTypeId
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Get dealing officers error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to fetch dealing officers'
            };
        }
    }

    /**
     * Get stages list
     * Equivalent to .NET bindStages method
     */
    async getStages() {
        try {
            const response = await this.apiClient.get('/jd/stages');
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Get stages error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to fetch stages'
            };
        }
    }

    /**
     * Get disciplines for an application
     * Equivalent to .NET binDisciplineAndGroup method with mode 68
     */
    async getDisciplines({ appId, labTypeId }) {
        try {
            const response = await this.apiClient.post('/jd/disciplines', {
                appId,
                labTypeId
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Get disciplines error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to fetch disciplines'
            };
        }
    }

    /**
     * Get groups for an application
     * Equivalent to .NET binDisciplineAndGroup method with mode 69
     */
    async getGroups({ appId, labTypeId }) {
        try {
            const response = await this.apiClient.post('/jd/groups', {
                appId,
                labTypeId
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Get groups error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to fetch groups'
            };
        }
    }

    /**
     * Allot applications to Joint Director/Dealing Officer
     * Equivalent to .NET btnAcceptMulti_Click method
     */
    async allotApplications({ applicationIds, allotedToId, password, orgId, userTypeId }) {
        try {
            const response = await this.apiClient.post('/jd/applications/allot', {
                applicationIds,
                allotedToId,
                password,
                orgId,
                userTypeId
            });
            // Return the response data directly since backend already wraps it
            return response.data;
        } catch (error) {
            console.error('Allot applications error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to allot applications'
            };
        }
    }

    /**
     * Export applications to Excel
     * Equivalent to .NET btnExporToExcel_Click method
     */
    async exportApplications({ appType, orgId, userTypeId, searchKeyword = '', dealingOfficer = null, stage = null }) {
        try {
            console.log('JD Export API - Request parameters:', {
                appType,
                orgId,
                userTypeId,
                searchKeyword,
                dealingOfficer,
                stage
            });

            const response = await this.apiClient.post('/jd/applications/export', {
                appType,
                orgId,
                userTypeId,
                searchKeyword,
                dealingOfficer,
                stage
            });

            console.log('JD Export API - Response:', response.data);

            return {
                success: true,
                data: response.data?.data || response.data
            };
        } catch (error) {
            console.error('Export applications error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to export applications'
            };
        }
    }

    /**
     * Accept application from Assessment Acceptance tab
     */
    async acceptApplication({ appId, orgId, jdId, userTypeId }) {
        try {
            const response = await this.apiClient.post('/jd/applications/accept', {
                appId,
                orgId,
                jdId,
                userTypeId
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Accept application error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to accept application'
            };
        }
    }

    /**
     * Reject application from Assessment Acceptance tab
     */
    async rejectApplication({ appId, orgId, jdId, userTypeId, remarks }) {
        try {
            const response = await this.apiClient.post('/jd/applications/reject', {
                appId,
                orgId,
                jdId,
                userTypeId,
                remarks
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Reject application error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to reject application'
            };
        }
    }

    /**
     * Get application details
     * Equivalent to .NET viewApplication page with LabApplications_Acts stored procedure mode 52
     */
    async getApplicationDetails({ appId, orgId, userTypeId }) {
        try {
            const response = await this.apiClient.post('/jd/applications/details', {
                mode: 52,
                appId,
                orgId,
                userTypeId
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Get application details error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to fetch application details'
            };
        }
    }

    /**
     * Get application status and stage information
     * Equivalent to .NET statusCAB method and getAppStatusStage function
     */
    async getApplicationStatus({ appId }) {
        try {
            const response = await this.apiClient.post('/jd/applications/status', {
                appId
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Get application status error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to fetch application status'
            };
        }
    }

    /**
     * Navigate to application dashboard
     * Equivalent to .NET acceptDOLA command in rptrApps_OnItemCommand
     */
    async navigateToApplicationDashboard({ appId, userTypeId, orgId }) {
        try {
            // Store application ID in session equivalent
            sessionStorage.setItem('AppId', appId);
            sessionStorage.setItem('Park', '1');

            // Determine redirect URL based on user type
            let redirectUrl = '/main/jd/dashboard'; // Default JD dashboard

            if (userTypeId === '8') {
                redirectUrl = '/main/director/dashboard';
            } else if (userTypeId === '4') {
                redirectUrl = '/main/do/dashboard';
            }

            return {
                success: true,
                data: {
                    redirectUrl,
                    appId
                }
            };
        } catch (error) {
            console.error('Navigate to application dashboard error:', error);
            return {
                success: false,
                message: error.message || 'Failed to navigate to application dashboard'
            };
        }
    }

    /**
     * Get application history
     */
    async getApplicationHistory({ appId, orgId, userTypeId }) {
        try {
            const response = await this.apiClient.post('/jd/applications/history', {
                appId,
                orgId,
                userTypeId
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Get application history error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to fetch application history'
            };
        }
    }

    /**
     * Download application as PDF
     */
    async downloadApplicationPDF({ appId, orgId, userTypeId }) {
        try {
            const response = await this.apiClient.post('/jd/applications/download-pdf', {
                appId,
                orgId,
                userTypeId
            }, {
                responseType: 'blob'
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Download application PDF error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to download application PDF'
            };
        }
    }

    /**
     * Get assessors for an application
     */
    async getAssessors({ appId }) {
        try {
            const response = await this.apiClient.post('/jd/assessors', {
                appId
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Get assessors error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to fetch assessors'
            };
        }
    }

    /**
     * Accept an assessor
     */
    async acceptAssessor({ appId, assessorId, orgId, userTypeId }) {
        try {
            const response = await this.apiClient.post('/jd/assessors/accept', {
                appId,
                assessorId,
                orgId,
                userTypeId
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Accept assessor error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to accept assessor'
            };
        }
    }

    /**
     * Reject an assessor
     */
    async rejectAssessor({ appId, assessorId, remarks, orgId, userTypeId }) {
        try {
            const response = await this.apiClient.post('/jd/assessors/reject', {
                appId,
                assessorId,
                remarks,
                orgId,
                userTypeId
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Reject assessor error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to reject assessor'
            };
        }
    }

    /**
     * Send application to dealing officer (update stage to 19h)
     */
    async sendToDealingOfficer({ appId, orgId, userTypeId, password }) {
        try {
            const response = await this.apiClient.post('/jd/applications/send-to-do', {
                appId,
                orgId,
                userTypeId,
                password
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Send to dealing officer error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to send to dealing officer'
            };
        }
    }

    /**
     * Finalize assessment (update stage to 20)
     */
    async finalizeAssessment({ appId, jdOrgId, userTypeId, password }) {
        try {
            const response = await this.apiClient.post('/jd/applications/finalize-assessment', {
                appId,
                jdOrgId,
                userTypeId,
                password
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Finalize assessment error:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to finalize assessment'
            };
        }
    }

    /**
     * Format date to display format
     */
    formatDate(dateString) {
        if (!dateString) return 'N/A';

        // Handle array of dates
        if (Array.isArray(dateString)) {
            dateString = dateString[0];
        }

        let date;

        // Check if the date string is in DD/MM/YYYY format
        if (typeof dateString === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
            const [day, month, year] = dateString.split('/');
            // Convert to ISO format (YYYY-MM-DD) for proper parsing
            date = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
        } else {
            date = new Date(dateString);
        }

        if (isNaN(date.getTime())) return 'N/A';

        const options = { year: 'numeric', month: 'short', day: '2-digit' };
        return date.toLocaleDateString('en-US', options);
    }

    /**
     * Get display name for button type
     */
    getButtonTypeDisplayName(buttonType) {
        const displayNames = {
            'processCompleted': 'Process Completed',
            'toBeProcessed': 'To be Processed',
            'underProcess': 'Under Process',
            'allActiveCabs': 'All Active CABs',
            'closedCabs': 'Closed CABs'
        };
        return displayNames[buttonType] || 'Applications';
    }

    /**
     * Calculate days between two dates
     */
    calculateDaysBetween(startDate, endDate = new Date()) {
        if (!startDate) return null;

        const start = new Date(startDate);
        const end = new Date(endDate);

        if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return diffDays;
    }


}

// Export singleton instance
const jdApiService = new JDApiService();
export default jdApiService;
